import { Box, Typography, Grid } from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";
import { useMaterials } from "../hooks/useMaterials";
import { ProductCard } from "../ProductCard";
import { SupplierComparisonRefresh as SupplierComparison } from "../../..";
import { GutterScope } from "./GutterScope";
import { Package } from "lucide-react";
import { MaterialsSkeleton } from "../skeletons";

interface MaterialsProps {
    measurements: any;
    setIsNextStageEligible: (val: any) => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getProductOptions(subcategories: any[]): string[] {
    return Array.from(new Set<string>(subcategories.map((s) => s.Name)));
}

function getColorOptions(subcategories: any[], selectedProduct: string | null): any[] {
    return subcategories.filter((s) => s.Name === selectedProduct && s.Color);
}

function getPriceDisplay(selection: any, priceRanges: any, categoryName: string): { display: string; label: string } {
    const selectedProduct = selection.value;
    const unitPriceNum = Number(selection.unitPrice);
    if (!isNaN(unitPriceNum) && unitPriceNum > 0) {
        return { display: `$${unitPriceNum.toFixed(2)}`, label: "Unit Price" };
    }
    if (selectedProduct && priceRanges[selectedProduct]) {
        const r = priceRanges[selectedProduct];
        return { display: `$${r.min.toFixed(2)} - $${r.max.toFixed(2)}`, label: "Unit Price Range" };
    }
    if (priceRanges[categoryName]) {
        const r = priceRanges[categoryName];
        return { display: `$${r.min.toFixed(2)} - $${r.max.toFixed(2)}`, label: "Price Range" };
    }
    return { display: "-", label: "Price" };
}

// ─── Single Row ──────────────────────────────────────────────────────────────

interface MaterialRowProps {
    item: any;
    selection: any;
    selectedValues: any;
    priceRanges: any;
    onProductChange: (val: any) => void;
    onColorChange: (color: string | null, mId?: string) => void;
    onQuantityUpdate: (dir: "add" | "remove") => void;
    onRemove: () => void;
}

function MaterialRow({
    item,
    selection,
    selectedValues,
    priceRanges,
    onProductChange,
    onColorChange,
    onQuantityUpdate,
    onRemove,
}: MaterialRowProps) {
    const subcategories = item.Subcategories || [];

    const productOptions = getProductOptions(subcategories);

    // Only use the stored value if it actually exists in the options list.
    // If there's a mismatch (DB name ≠ option name), pass null to avoid the MUI warning.
    const selectedProduct = productOptions.includes(selection.value) ? selection.value : null;

    const matchingSubs = getColorOptions(subcategories, selectedProduct);
    const colorOptions = Array.from(new Set<string>(matchingSubs.map((s) => s.Color as string)));
    const { display: priceDisplay, label: priceLabel } = getPriceDisplay(selection, priceRanges, item.Name);

    const hasNoVal = !selection.value;
    const hasNoQty = !selection.quantity || selection.quantity === 0;

    return (
        <ProductCard
            categoryName={item.Name}
            units={Array.from(new Set(item.MeasurementType?.map((m: any) => m.Unit))).join(", ")}
            productOptions={productOptions}
            selectedProduct={selectedProduct}
            onProductChange={onProductChange}
            colorOptions={colorOptions}
            selectedColor={selection.color || null}
            onColorChange={(color) => {
                const matched = matchingSubs.find((s) => s.Color === color);
                onColorChange(color, matched?.Id);
            }}
            quantity={selection.quantity || 0}
            onQuantityUpdate={onQuantityUpdate}
            priceDisplay={priceDisplay}
            priceLabel={priceLabel}
            hasIssue={hasNoVal || hasNoQty}
            issueTitle={hasNoVal ? "Selection Required" : "Quantity is Zero"}
            dependents={item.DependentsOn?.map((dep: string) => ({ name: dep, isSatisfied: !!selectedValues[dep] }))}
            onRemove={onRemove}
        />
    );
}

// ─── Material Groups ─────────────────────────────────────────────────────────

const MATERIAL_GROUPS: { label: string; items: string[] }[] = [
    {
        label: "Primary Roofing",
        items: ["Field Shingles", "Hip & Ridge", "Starter", "Double Starter"],
    },
    {
        label: "Underlayment & Waterproofing",
        items: ["Field Dry In", "Ice & Water Shield", "Valley Liner"],
    },
    {
        label: "Perimeter & Edge Metal",
        items: ["Drip Edge / Eave", "Drip Edge / Rake", "Ridge Vent"],
    },
    {
        label: "Fasteners & Consumables",
        items: ["Nails (15 SQ/box)", "Nails (750 LF/box)", "Nails (12 SQ/box)", "Sealant / Adhesive", "Spray Paint"],
    },
];

const ALL_GROUPED_ITEMS = MATERIAL_GROUPS.flatMap((g) => g.items);

// ─── Main Component ──────────────────────────────────────────────────────────

function MaterialsContent({ measurements, setIsNextStageEligible }: MaterialsProps) {
    const {
        materialSchema,
        priceRanges,
        isLoading,
        missingProducts,
        setOpenAdditionalProductDialog,
        selectedValues,
        handleQuantityUpdate,
        handleProductChange,
        handleColorChange,
        handleClearProduct,
    } = useMaterials({ measurements });

    // Build grouped items from the loaded schema
    const getGroupItems = (groupItemNames: string[]) =>
        groupItemNames
            .map((name) => materialSchema.find((item: any) => item.Name === name))
            .filter(Boolean);

    const ungroupedItems = materialSchema.filter((item: any) => !ALL_GROUPED_ITEMS.includes(item.Name));

    const sectionHeaderSx = {
        color: "text.tertiary", mb: 1.5, mt: 3, textTransform: "uppercase",
        letterSpacing: "0.5px", fontSize: "11px", fontWeight: "xl",
    };

    const renderMaterialRow = (item: any) => (
        <Grid key={item.Name} xs={12}>
            <MaterialRow
                item={item}
                selection={selectedValues[item.Name] || {}}
                selectedValues={selectedValues}
                priceRanges={priceRanges}
                onProductChange={(val) => handleProductChange(val, item.Name)}
                onColorChange={(color, mId) => handleColorChange(item.Name, color, mId)}
                onQuantityUpdate={(dir) => handleQuantityUpdate(item.Name, dir)}
                onRemove={() => handleClearProduct(item.Name)}
            />
        </Grid>
    );

    return (
        <AnimatePresence mode="popLayout">
            {isLoading ? (
                <motion.div
                    key="skeleton"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <MaterialsSkeleton />
                </motion.div>
            ) : (
                <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Box>
                        <Box mb={2}>
                            <Typography level="title-md" startDecorator={<Package size={16} />}>
                                Material & Component Selection
                            </Typography>
                            <Typography level="body-sm" color="neutral">
                                Product specifications, manufacturer comparisons, and inventory counts.
                            </Typography>
                        </Box>

                        {/* Grouped Product Cards */}
                        {MATERIAL_GROUPS.map((group) => {
                            const items = getGroupItems(group.items);
                            if (items.length === 0) return null;
                            return (
                                <Box key={group.label}>
                                    <Typography level="title-sm" sx={sectionHeaderSx}>
                                        {group.label}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {items.map(renderMaterialRow)}
                                    </Grid>
                                </Box>
                            );
                        })}

                        {/* Gutters — uses its own sub-component */}
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Gutters
                        </Typography>
                        <GutterScope measurements={measurements} />

                        {/* Ungrouped / Other */}
                        {ungroupedItems.length > 0 && (
                            <Box>
                                <Typography level="title-sm" sx={sectionHeaderSx}>
                                    Other
                                </Typography>
                                <Grid container spacing={2}>
                                    {ungroupedItems.map(renderMaterialRow)}
                                </Grid>
                            </Box>
                        )}

                        {/* Missing Products */}
                        {missingProducts.length > 0 && (
                            <Box mt={4} mb={2} px={1.5}>
                                <Typography level="body-xs" color="warning" mb={1} sx={{ fontWeight: 600 }}>
                                    MISSING PRODUCTS ({missingProducts.length})
                                </Typography>
                                <Grid container spacing={2}>
                                    {missingProducts.map((pName: string) => (
                                        <Grid key={pName} xs={12} md={6}>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    border: "1px dashed",
                                                    borderColor: "warning.300",
                                                    borderRadius: "md",
                                                    bgcolor: "warning.50",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                                                    {pName}
                                                </Typography>
                                                <Typography
                                                    level="body-xs"
                                                    component="a"
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setOpenAdditionalProductDialog(true);
                                                    }}
                                                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                                                >
                                                    Add Missing
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}

                        {/* Supplier Comparison & Selection */}
                        <Box mt={4}>
                            <SupplierComparison type="Manufacturer" selectedValues={selectedValues} />
                        </Box>
                    </Box>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// ─── Export with context provider ────────────────────────────────────────────
// SelectedSupplierProvider is required by SupplierComparison to share the
// selected supplier state between the grid and the price display components.

export default function Materials({ measurements, setIsNextStageEligible }: MaterialsProps) {
    return <MaterialsContent measurements={measurements} setIsNextStageEligible={setIsNextStageEligible} />;
}
